import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowRightLeft, TrendingUp, TrendingDown, Minus, Users, Network, Package, Globe } from 'lucide-react';
import { toast } from 'sonner';
import SAR from '../components/SAR';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

const currentYear = new Date().getFullYear();
const currentWeek = (() => {
  const now = new Date();
  const jan1 = new Date(now.getFullYear(), 0, 1);
  const offset = (jan1.getDay()) % 7; // JS getDay: Sun=0
  const week1Start = new Date(jan1);
  week1Start.setDate(jan1.getDate() - offset);
  const diffDays = Math.floor((now - week1Start) / (24 * 60 * 60 * 1000));
  return Math.floor(diffDays / 7) + 1;
})();

const DIMENSIONS = [
  { value: 'employee', label: 'مقارنة موظف', icon: Users },
  { value: 'branch',   label: 'مقارنة فرع', icon: Network },
  { value: 'product',  label: 'مقارنة منتج', icon: Package },
  { value: 'country',  label: 'مقارنة دولة', icon: Globe },
];

export default function SuperWeekCompare({ branchScoped = false }) {
  const [dimension, setDimension] = useState('employee');
  const [yearA, setYearA] = useState(currentYear);
  const [yearB, setYearB] = useState(currentYear);
  const [weekA, setWeekA] = useState(Math.max(1, currentWeek - 1));
  const [weekB, setWeekB] = useState(currentWeek);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Optional filters
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterProduct, setFilterProduct] = useState('all');
  const [filterCountry, setFilterCountry] = useState('all');

  const apiBase = branchScoped ? '/api/analytics/branch' : '/api/super/analytics';
  const visibleDimensions = DIMENSIONS;

  // Load reference lists once
  useEffect(() => {
    // Products catalog (super_admin endpoint is open; branch fallback to /api/products)
    const productsReq = branchScoped
      ? axios.get(`${API_URL}/api/products`).catch(() => ({ data: [] }))
      : axios.get(`${API_URL}/api/super/products`).catch(() => ({ data: [] }));
    productsReq.then((r) => {
      const list = (r.data || []).map((p) => ({ id: p.id, name: p.name }));
      // Dedupe by name (branch products may repeat name across branches)
      const seen = new Set();
      const dedup = [];
      list.forEach((p) => { if (!seen.has(p.name)) { seen.add(p.name); dedup.push(p); } });
      setProducts(dedup);
    });
    // Countries catalog (open to all logged-in users)
    axios.get(`${API_URL}/api/countries`).catch(() => ({ data: [] })).then((r) => {
      const list = (r.data || []).filter((c) => c.active !== false).map((c) => c.name);
      setCountries(list);
    });

    if (branchScoped) {
      // Branch admin/supervisor — fetch only employees of their own branch
      axios.get(`${API_URL}/api/employees`).catch(() => ({ data: [] })).then((r) => {
        setEmployees((r.data || []).map((e) => ({ id: e.id, name: e.name })));
      });
      return;
    }
    Promise.all([
      axios.get(`${API_URL}/api/super/network-details`).catch(() => ({ data: { employees: [] } })),
      axios.get(`${API_URL}/api/branches`).catch(() => ({ data: [] })),
    ]).then(([nd, br]) => {
      const emps = (nd.data?.employees || []).map((e) => ({
        id: e.id,
        name: e.branch_name ? `${e.name} — ${e.branch_name}` : e.name,
        branch_id: e.branch_id,
      }));
      setEmployees(emps);
      setBranches((br.data || []).map((b) => ({ id: b.id, name: b.name })));
    });
  }, [branchScoped]);

  const fetchCompare = async () => {
    const wa = parseInt(weekA, 10);
    const wb = parseInt(weekB, 10);
    if (!wa || !wb || wa < 1 || wa > 53 || wb < 1 || wb > 53) {
      toast.error('رقم الأسبوع يجب أن يكون بين 1 و 53');
      return;
    }
    setLoading(true);
    try {
      const params = {
        dimension,
        week_a_year: yearA, week_a_num: wa,
        week_b_year: yearB, week_b_num: wb,
      };
      if (dimension === 'employee' && filterEmployee && filterEmployee !== 'all') {
        params.employee_id = filterEmployee;
      }
      if (!branchScoped && dimension === 'branch' && filterBranch && filterBranch !== 'all') {
        params.branch_id = filterBranch;
      }
      if (dimension === 'product' && filterProduct && filterProduct !== 'all') {
        params.product_name = filterProduct;
      }
      if (dimension === 'country' && filterCountry && filterCountry !== 'all') {
        params.country = filterCountry;
      }
      const r = await axios.get(`${API_URL}${apiBase}/week-compare`, { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل تحميل المقارنة');
    } finally {
      setLoading(false);
    }
  };

  const dimMeta = useMemo(() => DIMENSIONS.find((d) => d.value === dimension) || DIMENSIONS[0], [dimension]);
  const DimIcon = dimMeta.icon;

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-black text-primary flex items-center gap-2">
          <ArrowRightLeft className="h-7 w-7 text-brand_blue" />
          مقارنة مبيعات
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          قارن مبيعات أي موظف أو فرع بين أسبوعين (مثال: الأسبوع 19 مقابل الأسبوع 20). الأسابيع تبدأ يوم الأحد.
        </p>
      </div>

      <Card className="p-4 sm:p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="font-bold">نوع المقارنة</Label>
            <Select value={dimension} onValueChange={setDimension}>
              <SelectTrigger data-testid="dimension-select" className="text-right">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {visibleDimensions.map((d) => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {dimension === 'employee' ? (
            <div className="space-y-1.5">
              <Label className="font-bold">
                الموظف <span className="text-xs text-muted-foreground">(اختياري)</span>
              </Label>
              <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                <SelectTrigger data-testid="filter-employee-select" className="text-right">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">— كل الموظفين —</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : dimension === 'product' ? (
            <div className="space-y-1.5">
              <Label className="font-bold">
                المنتج <span className="text-xs text-muted-foreground">(اختياري)</span>
              </Label>
              <Select value={filterProduct} onValueChange={setFilterProduct}>
                <SelectTrigger data-testid="filter-product-select" className="text-right">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">— كل المنتجات —</SelectItem>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : dimension === 'country' ? (
            <div className="space-y-1.5">
              <Label className="font-bold">
                الدولة <span className="text-xs text-muted-foreground">(اختياري)</span>
              </Label>
              <Select value={filterCountry} onValueChange={setFilterCountry}>
                <SelectTrigger data-testid="filter-country-select" className="text-right">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">— كل الدول —</SelectItem>
                  {countries.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : branchScoped ? (
            <div className="space-y-1.5">
              <Label className="font-bold">الفرع</Label>
              <div
                data-testid="branch-scoped-locked-branch"
                className="h-10 px-3 flex items-center rounded-md border border-input bg-muted/40 text-foreground/80 text-sm"
              >
                <Network className="h-4 w-4 ml-2 text-brand_blue" />
                مقارنة إجمالي مبيعات فرعك بين الأسبوعَين
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label className="font-bold">
                الفرع <span className="text-xs text-muted-foreground">(اختياري)</span>
              </Label>
              <Select value={filterBranch} onValueChange={setFilterBranch}>
                <SelectTrigger data-testid="filter-branch-select" className="text-right">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">— كل الفروع —</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-lg border-2 border-brand_blue/30 bg-brand_blue/5">
            <p className="font-black text-brand_blue mb-3 text-sm">الأسبوع A</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">السنة</Label>
                <Input
                  type="number"
                  value={yearA}
                  onChange={(e) => setYearA(parseInt(e.target.value, 10) || currentYear)}
                  min="2020" max="2100"
                  data-testid="year-a-input"
                  className="text-right font-numbers"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">رقم الأسبوع</Label>
                <Select value={String(weekA)} onValueChange={(v) => setWeekA(parseInt(v, 10))}>
                  <SelectTrigger data-testid="week-a-input" className="text-right font-numbers">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {Array.from({ length: 53 }, (_, i) => i + 1).map((n) => (
                      <SelectItem key={n} value={String(n)} className="font-numbers">{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-lg border-2 border-amber-500/30 bg-amber-500/5">
            <p className="font-black text-amber-600 mb-3 text-sm">الأسبوع B</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">السنة</Label>
                <Input
                  type="number"
                  value={yearB}
                  onChange={(e) => setYearB(parseInt(e.target.value, 10) || currentYear)}
                  min="2020" max="2100"
                  data-testid="year-b-input"
                  className="text-right font-numbers"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">رقم الأسبوع</Label>
                <Select value={String(weekB)} onValueChange={(v) => setWeekB(parseInt(v, 10))}>
                  <SelectTrigger data-testid="week-b-input" className="text-right font-numbers">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {Array.from({ length: 53 }, (_, i) => i + 1).map((n) => (
                      <SelectItem key={n} value={String(n)} className="font-numbers">{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <Button onClick={fetchCompare} disabled={loading} data-testid="run-compare-btn" className="gap-2">
          <ArrowRightLeft className="h-4 w-4" />
          {loading ? 'جاري المقارنة...' : 'تشغيل المقارنة'}
        </Button>
      </Card>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4 border-2 border-brand_blue/40">
              <p className="text-xs text-muted-foreground">الأسبوع A — {dimMeta.label}</p>
              <p className="text-xs font-numbers text-brand_blue mt-1" dir="ltr">{data.week_a.label}</p>
              <p className="text-2xl font-black text-brand_blue mt-2 font-numbers">
                {fmt(data.totals.a_total)} <SAR />
              </p>
              <p className="text-xs text-foreground/70 mt-1 font-numbers">{data.totals.a_qty} عملية</p>
            </Card>
            <Card className="p-4 border-2 border-amber-500/40">
              <p className="text-xs text-muted-foreground">الأسبوع B — {dimMeta.label}</p>
              <p className="text-xs font-numbers text-amber-600 mt-1" dir="ltr">{data.week_b.label}</p>
              <p className="text-2xl font-black text-amber-600 mt-2 font-numbers">
                {fmt(data.totals.b_total)} <SAR />
              </p>
              <p className="text-xs text-foreground/70 mt-1 font-numbers">{data.totals.b_qty} عملية</p>
            </Card>
            <Card className="p-4 border-2 border-border">
              <p className="text-xs text-muted-foreground">الفرق الإجمالي (B − A)</p>
              {(() => {
                const d = data.totals.b_total - data.totals.a_total;
                const pct = data.totals.a_total ? (d / data.totals.a_total * 100) : 0;
                const cls = d > 0 ? 'text-success' : d < 0 ? 'text-destructive' : 'text-muted-foreground';
                const Ico = d > 0 ? TrendingUp : d < 0 ? TrendingDown : Minus;
                return (
                  <>
                    <p className={`text-2xl font-black mt-2 font-numbers flex items-center gap-2 ${cls}`}>
                      <Ico className="h-6 w-6" />
                      {d > 0 ? '+' : ''}{fmt(d)} <SAR />
                    </p>
                    <p className={`text-xs mt-1 font-numbers font-bold ${cls}`}>
                      {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                    </p>
                  </>
                );
              })()}
            </Card>
          </div>

          <Card className="p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-accent/30 flex items-center gap-2">
              <DimIcon className="h-5 w-5 text-brand_blue" />
              <h3 className="font-black">تفصيل {dimMeta.label}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-right py-3 px-4 font-bold">{
                      dimension === 'employee' ? 'الموظف'
                      : dimension === 'branch' ? 'الفرع'
                      : dimension === 'product' ? 'المنتج'
                      : 'الدولة'
                    }</th>
                    {dimension === 'employee' && <th className="text-right py-3 px-4 font-bold">الفرع</th>}
                    <th className="text-right py-3 px-4 font-bold text-brand_blue">A: المبيعات</th>
                    <th className="text-right py-3 px-4 font-bold text-brand_blue">A: العمليات</th>
                    <th className="text-right py-3 px-4 font-bold text-amber-600">B: المبيعات</th>
                    <th className="text-right py-3 px-4 font-bold text-amber-600">B: العمليات</th>
                    <th className="text-right py-3 px-4 font-bold">الفرق</th>
                    <th className="text-right py-3 px-4 font-bold">النسبة</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 ? (
                    <tr>
                      <td colSpan={dimension === 'employee' ? 8 : 7} className="text-center py-12 text-muted-foreground">
                        لا توجد بيانات في الأسبوعين المحددين
                      </td>
                    </tr>
                  ) : data.rows.map((r) => {
                    const cls = r.delta > 0 ? 'text-success' : r.delta < 0 ? 'text-destructive' : 'text-muted-foreground';
                    return (
                      <tr key={r.id} className="border-t border-border hover:bg-accent/30" data-testid={`compare-row-${r.id}`}>
                        <td className="py-3 px-4 font-bold">{r.name}</td>
                        {dimension === 'employee' && <td className="py-3 px-4 text-foreground/70">{r.branch_name}</td>}
                        <td className="py-3 px-4 font-numbers text-brand_blue">{fmt(r.a_total)}</td>
                        <td className="py-3 px-4 font-numbers text-foreground/70">{r.a_qty}</td>
                        <td className="py-3 px-4 font-numbers text-amber-600">{fmt(r.b_total)}</td>
                        <td className="py-3 px-4 font-numbers text-foreground/70">{r.b_qty}</td>
                        <td className={`py-3 px-4 font-numbers font-bold ${cls}`}>
                          {r.delta > 0 ? '+' : ''}{fmt(r.delta)}
                        </td>
                        <td className={`py-3 px-4 font-numbers font-bold ${cls}`}>
                          {r.delta_pct > 0 ? '+' : ''}{r.delta_pct}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
